import { useState, useCallback, useMemo, useEffect } from 'react';
import { QuestionnaireData } from '@/types/category';
import { useAuth } from '@/contexts/AuthContext';
import { useOptimizedUserPreferences } from '@/hooks/useOptimizedUserPreferences';
import { useProfileManagement } from '@/hooks/useProfileManagement';

type StepId = 'personal' | 'details' | 'voice' | 'review';

interface StepValidation {
  isValid: boolean;
  missingFields: string[];
}

export const useQuestionnaireSteps = (
  categoryId: string,
  hasBiography: boolean,
  sessionType: 'individual' | 'group',
  isGroupSession: boolean,
  clientData?: any, // Client data for practitioner-created sessions
  initialData?: Partial<QuestionnaireData> | null, // Saved preferences for this client + category
  isPractitionerMode?: boolean // Whether this is a practitioner creating a session
) => {
  // Get user data for prefilling
  const { user } = useAuth();
  const { preferences, getBackgroundMusicSelection } = useOptimizedUserPreferences();
  const { profile } = useProfileManagement();

  // Define steps - voice selection is part of questionnaire for all users (practitioner and regular)
  const steps: StepId[] = useMemo(() => {
    const baseSteps: StepId[] = ['personal', 'details', 'voice', 'review'];
    return baseSteps;
  }, []);

  const [currentStep, setCurrentStep] = useState(0);

  // Initialize form data with defaults
  const [formData, setFormData] = useState<Partial<QuestionnaireData>>({
    // Basic defaults
    voiceGender: 'female',
    specific_voice_id: 'female_default', // Default voice ID matching voiceGender
    voiceType: 'calm',
    sessionLength: '3 minutes', // Default as string format expected by backend
    backgroundSounds: true,
    includeBackgroundSounds: true,
    usePersonalBiography: sessionType === 'individual' && hasBiography,
    explicitLanguageLevel: categoryId === 'zen-af' ? 8 : 5,
    humorLevel: 'Moderate'
  });

  // Prefill from user preferences and profile when available
  useEffect(() => {
    const updates: Partial<QuestionnaireData> = {};

    // Prefill from user preferences (legacy system)
    if (preferences && preferences.length > 0) {
      // Prefill voice preferences
      const voiceGenderPref = preferences.find(p => p.preference_type === 'voice_gender');
      if (voiceGenderPref) {
        updates.voiceGender = voiceGenderPref.preference_value as 'male' | 'female';
      }

      const voiceTypePref = preferences.find(p => p.preference_type === 'voice_type');
      if (voiceTypePref) {
        updates.voiceType = voiceTypePref.preference_value;
      }

      // Prefill session preferences
      const sessionLengthPref = preferences.find(p => p.preference_type === 'session_length');
      if (sessionLengthPref) {
        // Convert to string format if it's stored as a number
        const lengthValue = sessionLengthPref.preference_value;
        if (typeof lengthValue === 'string' && lengthValue.includes('minutes')) {
          updates.sessionLength = lengthValue;
        } else {
          // Convert number to string format
          const lengthNum = parseInt(lengthValue);
          if (!isNaN(lengthNum)) {
            const minutes = lengthNum / 60;
            updates.sessionLength = `${minutes} minutes`;
          }
        }
      }

      // Prefill background music
      const backgroundMusicPref = preferences.find(p => p.preference_type === 'background_music_selection');
      if (backgroundMusicPref) {
        updates.backgroundMusicSelection = backgroundMusicPref.preference_value;
        updates.includeBackgroundSounds = true;
        updates.backgroundSounds = true;
      }
    }

    // Prefill from profile or client data (new comprehensive system)
    // Use clientData for practitioner-created sessions, otherwise use profile for regular users
    const dataSource = clientData || profile;

    if (dataSource) {
      // Basic profile info
      if (dataSource.first_name) updates.firstName = dataSource.first_name;
      if (dataSource.last_name) updates.lastName = dataSource.last_name;
      if (dataSource.biography) {
        updates.biography = dataSource.biography;
        updates.usePersonalBiography = sessionType === 'individual' && !!dataSource.biography;
      }

      // Personal demographic information
      if (dataSource.age) updates.age = dataSource.age;
      if (dataSource.occupation) updates.occupation = dataSource.occupation;
      if (dataSource.relationship_status) updates.relationship = dataSource.relationship_status;
      if (dataSource.location) updates.location = dataSource.location;
      if (dataSource.hobbies) updates.hobbies = dataSource.hobbies;
      if (dataSource.stressors) updates.stressors = dataSource.stressors;
      if (dataSource.goals) updates.goals = dataSource.goals;

      // Default session preferences (override user_preferences if available)
      // Only use these from profile, not from clientData (practitioners should set their own session preferences)
      if (!clientData && profile) {
        if (profile.default_voice_gender) {
          updates.voiceGender = profile.default_voice_gender as 'male' | 'female';
        }
        if (profile.default_voice_type) {
          updates.voiceType = profile.default_voice_type;
        }
        if (profile.default_session_length) {
          const minutes = profile.default_session_length / 60;
          updates.sessionLength = `${minutes} minute${minutes !== 1 ? 's' : ''}`;
        }
        if (profile.default_background_music) {
          updates.backgroundMusicSelection = profile.default_background_music;
          updates.includeBackgroundSounds = true;
          updates.backgroundSounds = true;
        }
        if (profile.default_environment) {
          updates.environment = profile.default_environment;
          updates.useEnvironment = true;
        }

        // Category-specific preferences
        if (profile.category_preferences && profile.category_preferences[categoryId]) {
          const categoryPrefs = profile.category_preferences[categoryId];

          // Apply category-specific fields
          Object.keys(categoryPrefs).forEach(key => {
            if (categoryPrefs[key] !== undefined && categoryPrefs[key] !== null && categoryPrefs[key] !== '') {
              (updates as any)[key] = categoryPrefs[key];
            }
          });
        }
      }
    }

    // Merge saved preferences (highest priority - overrides everything else)
    if (initialData) {
      Object.keys(initialData).forEach(key => {
        if (initialData[key as keyof QuestionnaireData] !== undefined &&
            initialData[key as keyof QuestionnaireData] !== null &&
            initialData[key as keyof QuestionnaireData] !== '') {
          (updates as any)[key] = initialData[key as keyof QuestionnaireData];
        }
      });
    }

    // Apply all updates at once if we have any
    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
    }
  }, [preferences, profile, sessionType, categoryId, clientData, initialData]);

  const currentStepId = steps[currentStep];
  const totalSteps = steps.length;

  // Update form data
  const updateFormData = useCallback((updates: Partial<QuestionnaireData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  // Validate current step
  const validateStep = useCallback((stepId: StepId): StepValidation => {
    const missingFields: string[] = [];
    let isValid = true;

    switch (stepId) {
      case 'personal':
        // Personal info validation
        if (sessionType === 'individual') {
          if (!formData.firstName?.trim()) {
            missingFields.push('First Name');
            isValid = false;
          }
        }
        break;

      case 'details':
        // Category-specific validation
        if (categoryId === 'zen-af') {
          if (sessionType === 'individual') {
            if (!formData.specificFocus?.trim()) {
              missingFields.push("What's bothering you");
              isValid = false;
            }
          } else {
            // Group session - check if practitioner mode
            if (!formData.groupFrustration?.trim()) {
              // Use appropriate label based on practitioner mode
              const label = isPractitionerMode
                ? "What's bothering your clients today"
                : "What's pissing off your group";
              missingFields.push(label);
              isValid = false;
            }
          }
          if (!formData.deeperReason?.trim()) {
            const label = sessionType === 'individual'
              ? 'Why this affects you'
              : (isPractitionerMode ? 'Why this affects your clients' : 'Why this affects your group');
            missingFields.push(label);
            isValid = false;
          }
        } else if (categoryId === 'yoga-meditation') {
          // For group sessions, check group-specific field names
          if (sessionType === 'group') {
            if (!formData.groupExperienceLevel) {
              missingFields.push('Group Experience Level');
              isValid = false;
            }
            // preferredStyle serves as the yoga style for groups
            if (!formData.preferredStyle) {
              missingFields.push('Yoga Style');
              isValid = false;
            }
          } else {
            // Individual sessions
            if (!formData.experienceLevel) {
              missingFields.push('Experience Level');
              isValid = false;
            }
            if (!formData.preferredStyle) {
              missingFields.push('Yoga Style');
              isValid = false;
            }
          }
        } else if (categoryId === 'sleep') {
          if (!formData.sessionType) {
            missingFields.push('Session Type');
            isValid = false;
          }
        } else if (categoryId === 'massage') {
          if (!formData.massageType) {
            missingFields.push('Massage Type');
            isValid = false;
          }
          if (!formData.targetBodyParts?.length) {
            missingFields.push('Target Body Parts');
            isValid = false;
          }
        }
        // Note: Categories like couples-connection, addiction-recovery-men,
        // relationship-decisions, sexual-wellness-women, and fitness rely on
        // the 'voice' step validation for required fields
        break;

      case 'voice':
        // Voice and session preferences validation
        if (!formData.voiceGender) {
          missingFields.push('Voice Gender');
          isValid = false;
        }
        if (!formData.sessionLength || formData.sessionLength === '') {
          missingFields.push('Session Length');
          isValid = false;
        }
        // Only require background music selection if background sounds are enabled
        if ((formData.backgroundSounds ?? true) && formData.backgroundMusicSelection === undefined) {
          missingFields.push('Background Music');
          isValid = false;
        }
        break;

      case 'review':
        // Review step is always valid if we got here
        isValid = true;
        break;
    }

    return { isValid, missingFields };
  }, [categoryId, sessionType, formData]);

  // Check if can proceed to next step
  const canGoNext = useCallback(() => {
    if (currentStepId === 'review') return true; // Can always submit from review
    return validateStep(currentStepId).isValid;
  }, [currentStepId, validateStep]);

  // Navigation functions
  const nextStep = useCallback(() => {
    if (currentStep < steps.length - 1 && canGoNext()) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, steps.length, canGoNext]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback((stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setCurrentStep(stepIndex);
    }
  }, [steps.length]);

  // Check if on first or last step
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  return {
    // Current state
    currentStep,
    currentStepId,
    steps,
    totalSteps,
    formData,

    // Actions
    updateFormData,
    nextStep,
    prevStep,
    goToStep,

    // Validation
    canGoNext,
    validateStep,

    // Helpers
    isFirstStep,
    isLastStep
  };
};